import { getConnectionById } from '../../connections/utils/connectionLookup';
import type {
  SecurityManagerView,
  SecurityObjectType as WorkspaceSecurityObjectType,
} from '../../workspace/core/types/workspaceTypes';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

export type ExplorerSecurityNodeType =
  | 'login'
  | 'server-role'
  | 'database-user'
  | 'database-role'
  | 'schema'
  | 'login-role'
  | 'group-role'
  | 'oracle-user'
  | 'oracle-role';

export type SecurityExplorerNodeContext = {
  nodeType: ExplorerSecurityNodeType;
  connectionId: string;
  database?: string;
  principalId: string;
  principalName: string;
  engine: 'mssql' | 'pgsql' | 'mysql' | 'oracle';
  isSystem?: boolean;
};

export type ScriptOperation = 'create' | 'alter' | 'drop';

export function getConnectionContext(connectionId: string): {
  connectionName: string;
  serverHost: string;
  defaultDatabase: string;
} | null {
  // Get connection info (supports both saved connections and browser mode)
  const { activeConnection, connection } = getConnectionById(connectionId);

  // Require at least an active connection
  if (!activeConnection) return null;

  // Use connection config from either saved connection or active connection
  const connectionConfig = connection || activeConnection.connection;

  return {
    connectionName: safeConnectionAlias(connectionConfig, 'Unnamed Connection'),
    serverHost: safeConnectionServer(activeConnection.connection),
    defaultDatabase: activeConnection.connection.database || '',
  };
}

export function mapExplorerNodeTypeToSecurityManagerType(
  nodeType: ExplorerSecurityNodeType,
): {
  view: SecurityManagerView;
  objectType: WorkspaceSecurityObjectType;
} | null {
  switch (nodeType) {
    case 'login':
      return { view: 'server', objectType: 'login' };
    case 'server-role':
      return { view: 'server', objectType: 'server-role' };
    case 'database-user':
      return { view: 'database', objectType: 'user' };
    case 'database-role':
      return { view: 'database', objectType: 'database-role' };
    case 'schema':
      return { view: 'database', objectType: 'schema' };
    case 'login-role':
      return { view: 'server', objectType: 'login-role' };
    case 'group-role':
      return { view: 'server', objectType: 'group-role' };
    case 'oracle-user':
      return { view: 'server', objectType: 'oracle-user' };
    case 'oracle-role':
      return { view: 'server', objectType: 'oracle-role' };
    default:
      return null;
  }
}

export function formatWarningsAsSqlComments(warnings: string[] | undefined): string {
  if (!warnings || warnings.length === 0) return '';
  return warnings.map((w) => `-- WARNING: ${w}`).join('\n');
}

export function formatNotesAsSqlComments(notes: string[]): string {
  if (notes.length === 0) return '';
  return notes.map((n) => `-- NOTE: ${n}`).join('\n');
}
